import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Card, Table, Button, Space, Select, message } from "antd";
import axios from "axios";

const computeExpiry = (createdAt, validDays) => {
  if (!createdAt || !validDays) return "-";
  const d = new Date(createdAt);
  d.setDate(d.getDate() + Number(validDays));
  return d.toISOString().slice(0, 10);
};

const formatDiscountValue = (type, amount, percent) => {
  const t = String(type || "").toLowerCase();
  if (t === "percent") return `${percent || "-"}%`;
  return `RM ${amount || "-"}`;
};

const AccountManagerVouchers = ({ accountManagerId }) => {
  const [user, setUser] = useState();
  const [catalog, setCatalog] = useState([]);
  const [attachedVoucherIds, setAttachedVoucherIds] = useState([]);
  const [selectedVoucherId, setSelectedVoucherId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true);
      const params = { accountManagerId };
      const res = await axios.get(`/api/account-manager-info`, { params });

      const data = res?.data?.data;
      setUser(data);

      const raw = data?.accountManagerVouchers ?? [];
      const ids = raw.map((item) => item?.objectId).filter(Boolean);

      setAttachedVoucherIds(ids);
    } finally {
      setLoading(false);
    }
  }, [accountManagerId]);

  const fetchVouchers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/vouchers", { params: { isActive: true } });
      const list = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
      setCatalog(list);
    } catch (e) {
      message.error("Failed to fetch active vouchers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVouchers();
    fetchUser();
  }, [fetchUser, fetchVouchers]);

  const attachedVouchers = useMemo(
    () => catalog.filter((v) => attachedVoucherIds.includes(v._id)),
    [catalog, attachedVoucherIds]
  );

  const availableToAttach = useMemo(() => {
    const attachedSet = new Set(attachedVoucherIds);
    return catalog.filter((v) => !attachedSet.has(v._id));
  }, [catalog, attachedVoucherIds]);

  const saveVouchers = async (nextIds) => {
    setSaving(true);
    try {
      await axios.put("/api/user/add-voucher-to-account-manager", {
        voucherIds: nextIds,
        accountManagerId,
      });
      setAttachedVoucherIds(nextIds);
      message.success("Account manager vouchers updated.");
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || "Failed to update account manager vouchers.");
    } finally {
      setSaving(false);
    }
  };

  const onAttach = () => {
    if (!selectedVoucherId) {
      message.warning("Please select a voucher to attach.");
      return;
    }

    if (attachedVoucherIds.includes(selectedVoucherId)) {
      message.info("Voucher already attached.");
      return;
    }

    const nextIds = [...attachedVoucherIds, selectedVoucherId];
    setSelectedVoucherId(null);
    saveVouchers(nextIds);
  };

  const onRemove = (id) => {
    const nextIds = attachedVoucherIds.filter((vId) => vId !== id);
    saveVouchers(nextIds);
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
    },
    {
      title: "Discount Value",
      key: "discountValue",
      width: 160,
      render: (_, record) => formatDiscountValue(record.type, record.amount, record.percent),
    },
    {
      title: "Min Spend",
      dataIndex: "minSpend",
      key: "minSpend",
      width: 140,
      render: (v) => (v ? `RM ${v}` : "No Min"),
    },
    {
      title: "Valid Days",
      dataIndex: "validDays",
      key: "validDays",
      width: 120,
      render: (v) => (v ? v : "-"),
    },
    {
      title: "Expires On",
      key: "expiresOn",
      width: 140,
      render: (_, record) => computeExpiry(record.createdAt, record.validDays),
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (_, record) => (
        <Button danger onClick={() => onRemove(record._id)} disabled={saving} loading={saving}>
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Card bordered>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        <Space wrap>
          <Select
            showSearch
            allowClear
            placeholder="Select an active voucher to attach"
            style={{ minWidth: 360 }}
            value={selectedVoucherId}
            onChange={(val) => setSelectedVoucherId(val)}
            loading={loading}
            optionFilterProp="label"
            options={availableToAttach.map((v) => ({
              value: v._id,
              label: `${v.name} — Discount Value: ${formatDiscountValue(v.type, v.amount, v.percent)}${
                v.minSpend ? `, Min Spend: ${v.minSpend}` : ""
              }`,
            }))}
          />
          <Button type="primary" onClick={onAttach} disabled={!selectedVoucherId || saving} loading={saving}>
            Attach Voucher
          </Button>
        </Space>

        <Table rowKey="_id" size="middle" columns={columns} dataSource={attachedVouchers} pagination={false} />
      </Space>
    </Card>
  );
};

export default AccountManagerVouchers;
